/* eslint-disable import/no-named-as-default */
import runPromptOpenAi from "./openAiClient";
import runPromptOpenRouter from "./openRouterClient";
import runPromptLmStudio from "./lmStudioClient";
/* eslint-enable import/no-named-as-default */
import { PdfExcerpt, RunPromptOptions, RunPromptResult } from "./aiCommon";
import { Reference } from "./reference";

export type LlmProvider = "openai" | "openrouter" | "lmstudio";

export type RunPromptWithProviderOptions = RunPromptOptions & {
  provider?: LlmProvider;
};

function resolveProvider(explicit?: LlmProvider): LlmProvider {
  if (explicit) return explicit;
  const fromEnv = (
    process.env.LLM_PROVIDER ||
    process.env.REACT_APP_LLM_PROVIDER ||
    ""
  ).toLowerCase();
  if (fromEnv === "openrouter") return "openrouter";
  if (fromEnv === "lmstudio") return "lmstudio";
  return "openai";
}

export async function runPromptWithProvider(
  sourceDocument: PdfExcerpt,
  reference: Reference,
  options: RunPromptWithProviderOptions = {}
): Promise<RunPromptResult> {
  const provider = resolveProvider(options.provider);

  const invoke = async (): Promise<RunPromptResult> => {
    if (provider === "openrouter") {
      return runPromptOpenRouter(sourceDocument, reference, options);
    }
    if (provider === "lmstudio") {
      return runPromptLmStudio(sourceDocument, reference, options);
    }
    return runPromptOpenAi(sourceDocument, reference, options);
  };

  const isEmptyLike = (text: string | undefined): boolean => {
    if (!text) return true;
    // Treat outputs that are only whitespace/newlines as empty
    return text.replace(/\s/g, "").length === 0;
  };

  const maxAttempts = 3; // initial try + 2 retries

  const attemptRun = async (attemptNum: number): Promise<RunPromptResult> => {
    try {
      const result = await invoke();
      if (!isEmptyLike(result.text)) {
        return result;
      }
      if (attemptNum >= maxAttempts) return result;
      // eslint-disable-next-line no-console
      console.warn(
        `runPromptWithProvider: empty-like output received (attempt ${attemptNum}/${
          maxAttempts - 1
        }), retrying...`
      );
      await new Promise((r) => setTimeout(r, 400 * attemptNum));
      return attemptRun(attemptNum + 1);
    } catch (err) {
      if (attemptNum >= maxAttempts) throw err;
      // eslint-disable-next-line no-console
      console.warn(
        `runPromptWithProvider: error on attempt ${attemptNum}/${
          maxAttempts - 1
        }, retrying...`,
        err
      );
      await new Promise((r) => setTimeout(r, 400 * attemptNum));
      return attemptRun(attemptNum + 1);
    }
  };

  return attemptRun(1);
}

export default runPromptWithProvider;
